import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/dashboard.css';
import Header from '../component/header';

const VoterListMenu = () => {
    const navigate = useNavigate();
    const { language } = useAppId();

    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            // Page titles
            title: "Voter List",
            subtitle: "Access and manage voter database efficiently.",

            // Menu items
            masterSearch: "Master Search",
            quickSearch: "Quick Search",
            speakSearch: "Speak & Search",
            scanIdCard: "Scan Id-card",
            boothWise: "Booth Wise",
            addressWise: "Address Wise",
            votingPlaceWise: "Voting Place Wise",
            surnameWise: "Surname Wise",
            ageWise: "Age Wise"
        },

        hi: {
            // Page titles
            title: "मतदाता सूची",
            subtitle: "मतदाता डेटाबेस तक पहुंचें और कुशलतापूर्वक प्रबंधित करें।",

            // Menu items
            masterSearch: "मास्टर खोज",
            quickSearch: "त्वरित खोज",
            speakSearch: "बोलकर खोजें",
            scanIdCard: "आईडी कार्ड स्कैन करें",
            boothWise: "बूथ वार",
            addressWise: "पता वार",
            votingPlaceWise: "मतदान स्थल वार",
            surnameWise: "उपनाम वार",
            ageWise: "आयु वार"
        },

        gu: {
            // Page titles
            title: "મતદાર યાદી",
            subtitle: "મતદાર ડેટાબેઝને ઍક્સેસ કરો અને કાર્યક્ષમ રીતે સંચાલિત કરો.",

            // Menu items
            masterSearch: "માસ્ટર શોધ",
            quickSearch: "ઝડપી શોધ",
            speakSearch: "બોલીને શોધો",
            scanIdCard: "આઈડી કાર્ડ સ્કેન કરો",
            boothWise: "બૂથ વાઈઝ",
            addressWise: "સરનામું વાઈઝ",
            votingPlaceWise: "મતદાન સ્થળ વાઈઝ",
            surnameWise: "ઉપનામ વાઈઝ",
            ageWise: "ઉંમર વાઈઝ"
        }
    };

    const txt = t[lang];

    const handleNavigate = (path) => {
        setTimeout(() => {
            navigate(path);
        }, 150);
    };

    const menuItems = [
        { title: txt.masterSearch, icon: <SearchChartIcon />, path: "/master_search", color: "#3b82f6", delay: "0.1s" },
        { title: txt.quickSearch, icon: <QuickSearchIcon />, path: "/quick_search", color: "#06b6d4", delay: "0.15s" },
        { title: txt.speakSearch, icon: <MicIcon />, path: "/speak_and_search", color: "#0ea5e9", delay: "0.2s" },
        { title: txt.scanIdCard, icon: <IdCardIcon />, path: "/scan_id", color: "#64748b", delay: "0.25s" },
        { title: txt.boothWise, icon: <BoothCurtainIcon />, path: "/display_total_booth", color: "#8b5cf6", delay: "0.3s" },
        { title: txt.votingPlaceWise, icon: <VotingPlaceIcon />, path: "/display_polling_location_wise_voters", color: "#6366f1", delay: "0.4s" },
        { title: txt.surnameWise, icon: <SurnameIcon />, path: "/dis_all_surname", color: "#f97316", delay: "0.45s" },
        { title: txt.ageWise, icon: <AgeWiseIcon />, path: "/age_wise_search", color: "#10b981", delay: "0.5s" },
    ];

    return (
        <div className="app-container dashboard-page p-4">

            {/* Header Component Integrated Here */}
            <Header
                title={txt.title}
            />

            {/* Grid Content */}
            <div className="dashboard-content container-fluid mt-4">
                <div className="row g-4 justify-content-center">
                    {menuItems.map((item, index) => (
                        <div key={index} className="col-6 col-md-4 col-lg-3 col-xl-2 fade-in-up" style={{ animationDelay: item.delay }}>
                            <div
                                className="dash-card main-card h-100"
                                onClick={() => handleNavigate(item.path)}
                                style={{ '--hover-color': item.color }}
                            >
                                <div className="icon-wrapper bounce-on-hover" style={{ backgroundColor: `${item.color}15`, color: item.color }}>
                                    {item.icon}
                                </div>
                                <h3 className="card-title" style={{ fontSize: '15px' }}>{item.title}</h3>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Inline CSS for specific page enhancements */}
            <style jsx>{`
                /* Enhanced Fade In Up for Cards */
                .fade-in-up {
                    opacity: 0;
                    animation: fadeInUp 0.7s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
                }

                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* Card Hover Effects */
                .dash-card {
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
                    border: 1px solid #f1f5f9;
                    background: white;
                    border-radius: 16px;
                    padding: 24px 16px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    position: relative;
                    overflow: hidden;
                }

                .dash-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1);
                    border-color: var(--hover-color, #e2e8f0);
                }

                /* Icon Wrapper Styling */
                .icon-wrapper {
                    width: 64px;
                    height: 64px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 16px;
                    transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
                }

                /* Bounce/Rotate Icon on Hover */
                .dash-card:hover .icon-wrapper {
                    transform: scale(1.15) rotate(5deg);
                }

                /* Text Styling */
                .card-title {
                    font-weight: 700;
                    color: #334155;
                    transition: color 0.3s ease;
                    margin: 0;
                }

                .dash-card:hover .card-title {
                    color: var(--hover-color);
                }

                /* Click feedback (active state) */
                .dash-card:active {
                    transform: scale(0.96);
                }
            `}</style>
        </div>
    );
};

// --- ICONS ---
const SearchChartIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        <path d="M11 7v8" strokeWidth="2" strokeOpacity="0.3" />
        <path d="M8 11v4" strokeWidth="2" strokeOpacity="0.5" />
        <path d="M14 9v6" strokeWidth="2" strokeOpacity="0.8" />
    </svg>
);

const QuickSearchIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        <path d="M7 11h2l2-3 2 6 2-3h2" />
    </svg>
);

const MicIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
        <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
        <line x1="12" y1="19" x2="12" y2="23"></line>
        <line x1="8" y1="23" x2="16" y2="23"></line>
    </svg>
);

const IdCardIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
        <circle cx="12" cy="10" r="3"></circle>
        <path d="M12 13v1"></path>
        <path d="M8 17h8"></path>
        <line x1="12" y1="2" x2="12" y2="4"></line>
    </svg>
);

const BoothCurtainIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18" />
        <path d="M5 21V7l8-4 8 4v14" />
        <path d="M5 7h16" />
        <path d="M12 21V10" />
        <path d="M9 10v11" strokeOpacity="0.5" />
        <path d="M15 10v11" strokeOpacity="0.5" />
    </svg>
);

const AddressIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
        <circle cx="12" cy="10" r="3"></circle>
        <path d="M8 21h8" strokeOpacity="0.5" />
    </svg>
);

const VotingPlaceIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="16" height="16" rx="2"></rect>
        <path d="M12 8v8"></path>
        <path d="M8 12h8"></path>
        <path d="M12 16l-3-3"></path>
        <path d="M12 16l3-3"></path>
    </svg>
);

const SurnameIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
);

const AgeWiseIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="5" r="2"></circle>
        <path d="M12 7v5" />
        <path d="M9 12h6" />
        <path d="M10 22l2-5 2 5" />
        <path d="M18 20l2-8" strokeOpacity="0.5" />
        <path d="M6 20l-2-8" strokeOpacity="0.5" />
        <circle cx="19" cy="8" r="1.5" strokeOpacity="0.5" />
        <circle cx="5" cy="8" r="1.5" strokeOpacity="0.5" />
    </svg>
);

export default VoterListMenu;